import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Advanced } from '../models/advanced';
import { State } from '../redux/redux.state';
import { Status } from '../redux/redux.status';
import client from '../clients/client';

export type SettingType =
  | 'customcss'
  | 'customjs'
  | 'widgetcss'
  | 'widgetfeatures';

type AdvancedSettingsState = State<Advanced | undefined> & {
  postCustomCssStatus: Status;
  postCustomJsStatus: Status;
  postWidgetCssStatus: Status;
  postWidgetFeaturesStatus: Status;
};

const initialState: AdvancedSettingsState = {
  data: undefined,
  status: 'idle',
  postCustomCssStatus: 'idle',
  postCustomJsStatus: 'idle',
  postWidgetCssStatus: 'idle',
  postWidgetFeaturesStatus: 'idle',
};

export const fetchAdvancedSettings = createAsyncThunk<Advanced, string>(
  'fetchAdvancedSettings',
  async (shopId: string, { rejectWithValue }) => {
    try {
      const { data } = await client.get(`/features/advanced/${shopId}`);
      return data;
    } catch (err: any) {
      const { status, data } = err.response;
      return rejectWithValue({ status, errorMessage: data });
    }
  }
);

export const postAdvancedSetting = createAsyncThunk<
  any,
  { value: string; shopId: string; settingType: SettingType }
>(
  'postAdvancedSettings',
  async ({ value, shopId, settingType }, { rejectWithValue }) => {
    try {
      const { data } = await client.post(`/features/advanced`, {
        value,
        shopId,
        settingType,
      });
      return data;
    } catch (err: any) {
      const { status, data } = err.response;
      return rejectWithValue({ status, errorMessage: data });
    }
  }
);

const advancedSettingsSlice = createSlice({
  name: 'advancedSettings',
  initialState,
  reducers: {
    updateWidgetFeatures: (state, action) => {
      if (state.data) {
        state.data.widgetFeatures = action.payload;
      }
    },
    resetStatus: (state) => {
      state.postCustomCssStatus = 'idle';
      state.postCustomJsStatus = 'idle';
      state.postWidgetCssStatus = 'idle';
      state.postWidgetFeaturesStatus = 'idle';
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchAdvancedSettings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAdvancedSettings.fulfilled, (state, action) => {
        state.status = 'successful';
        state.data = action.payload;
      })
      .addCase(fetchAdvancedSettings.rejected, (state) => {
        state.status = 'error';
      })
      .addCase(postAdvancedSetting.pending, (state, action) => {
        switch (action.meta.arg.settingType) {
          case 'customcss':
            state.postCustomCssStatus = 'loading';
            break;
          case 'customjs':
            state.postCustomJsStatus = 'loading';
            break;
          case 'widgetcss':
            state.postWidgetCssStatus = 'loading';
            break;
          case 'widgetfeatures':
            state.postWidgetFeaturesStatus = 'loading';
            break;
        }
      })
      .addCase(postAdvancedSetting.fulfilled, (state, action) => {
        switch (action.meta.arg.settingType) {
          case 'customcss':
            state.postCustomCssStatus = 'successful';
            break;
          case 'customjs':
            state.postCustomJsStatus = 'successful';
            break;
          case 'widgetcss':
            state.postWidgetCssStatus = 'successful';
            break;
          case 'widgetfeatures':
            state.postWidgetFeaturesStatus = 'successful';
            break;
        }
      })
      .addCase(postAdvancedSetting.rejected, (state, action) => {
        switch (action.meta.arg.settingType) {
          case 'customcss':
            state.postCustomCssStatus = 'error';
            break;
          case 'customjs':
            state.postCustomJsStatus = 'error';
            break;
          case 'widgetcss':
            state.postWidgetCssStatus = 'error';
            break;
          case 'widgetfeatures':
            state.postWidgetFeaturesStatus = 'error';
            break;
        }
      }),
});

export const { resetStatus, updateWidgetFeatures } =
  advancedSettingsSlice.actions;

export default advancedSettingsSlice.reducer;
